// Coaches vs. Skills home layout switch.
// Coaches = the roster grouped under the 4 coach umbrellas. Skills (the
// default) = the flattened, skills-forward board — one card per product.

import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

export type BentoViewMode = "coaches" | "skills";

const VIEW_MODE_STORAGE_KEY = "parIntelligenceViewMode";

const readStoredViewMode = (): BentoViewMode => {
    if (typeof window === "undefined") {
        return "skills";
    }
    // Skills is the default; only an explicit stored "coaches" opts out of it.
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "coaches"
        ? "coaches"
        : "skills";
};

interface BentoViewModeContextValue {
    mode: BentoViewMode;
    setMode: (mode: BentoViewMode) => void;
}

const BentoViewModeContext = createContext<BentoViewModeContextValue | null>(
    null
);

interface ProviderProps {
    children: ReactNode;
}

export const BentoViewModeProvider: FC<ProviderProps> = ({children}) => {
    const [mode, setModeState] = useState<BentoViewMode>(readStoredViewMode);

    const setMode = useCallback((next: BentoViewMode) => {
        setModeState(next);
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    }, [mode]);

    return (
        <BentoViewModeContext.Provider value={{mode, setMode}}>
            {children}
        </BentoViewModeContext.Provider>
    );
};

/** Use inside any Bento component to read/set the Coaches vs. Skills home layout. */
export function useBentoViewMode(): BentoViewModeContextValue {
    const context = useContext(BentoViewModeContext);
    if (!context) {
        throw new Error(
            "useBentoViewMode must be used within BentoViewModeProvider"
        );
    }
    return context;
}
